import { Canvas } from "fabric";
import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "@/hooks/use-toast";
import { LayerInfo, GroupInfo, DisplayLayerInfo, GroupType } from "@/components/layers/types";
import { CustomFabricObject, CustomCanvas } from "@/types/fabric";
import { debounce, DebouncedFunc } from "lodash";

// Type for group definition to ensure type safety
interface GroupDefinition {
  id: string;
  name: string;
  type: GroupType;
  filter: (obj: CustomFabricObject) => boolean;
}

export const useLayerManagement = (canvas: Canvas | null) => {
  // Create a single state object to manage all layer types
  const [layerState, setLayerState] = useState<{
    regularLayers: LayerInfo[];
    groups: GroupInfo[];
  }>({
    regularLayers: [],
    groups: []
  });
  
  // Create a debounced version of the updateLayers function with proper typing
  const updateLayersRef = useRef<DebouncedFunc<() => void>>();
  
  // Track layer names to ensure consistency
  const layerNamesRef = useRef<Map<string, string>>(new Map());
  
  // Track group names for persistence
  const groupNamesRef = useRef<Map<string, string>>(new Map());
  
  // Helper function to ensure unique layer names
  const getUniqueLayerName = useCallback((obj: CustomFabricObject, index: number) => {
    const objId = obj.data?.id || `layer-${index}`;
    
    // If we already have a stored name for this object, use it
    if (layerNamesRef.current.has(objId)) {
      return layerNamesRef.current.get(objId) as string;
    }
    
    // If the object already has a custom name set, use it
    if (obj.data?.name) {
      const existingNames = new Set(Array.from(layerNamesRef.current.values()));
      let uniqueName = obj.data.name;
      let counter = 1;
      
      // Still ensure the name is unique
      while (existingNames.has(uniqueName)) {
        uniqueName = `${obj.data.name} ${counter}`;
        counter++;
      }
      
      layerNamesRef.current.set(objId, uniqueName);
      return uniqueName;
    }
    
    // Otherwise generate a new name
    let baseName: string;
    
    // Try to get a meaningful name from the object data or type
    if (obj.type === 'image') {
      baseName = 'Image';
    } else if (obj.type === 'path') {
      if (obj.data?.type === 'drawing') {
        baseName = 'Drawing';
      } else if (obj.data?.type === 'scalpel' || obj.data?.type === 'incision') {
        baseName = 'Incision';
      } else {
        baseName = 'Path';
      }
    } else {
      // Fallback to object type or generic "Layer"
      baseName = (obj.type || 'Layer').charAt(0).toUpperCase() + (obj.type || 'Layer').slice(1);
    }
    
    // Now ensure uniqueness of the name
    let uniqueName = baseName;
    let counter = 1;
    
    const existingNames = new Set(Array.from(layerNamesRef.current.values()));
    
    while (existingNames.has(uniqueName)) {
      uniqueName = `${baseName} ${counter}`;
      counter++;
    }
    
    // Store the name for future reference
    layerNamesRef.current.set(objId, uniqueName);
    return uniqueName;
  }, []);

  // Update layers list when canvas objects change
  useEffect(() => {
    if (!canvas) return;

    // Define the updateLayers function that will be debounced
    const processCanvasObjects = () => {
      const objects = canvas.getObjects() as CustomFabricObject[];
      
      // Group definitions with improved type safety
      const groupDefinitions: GroupDefinition[] = [
        { id: 'ruler-measurements', name: 'Ruler Measurements', type: 'ruler', 
          filter: (obj: CustomFabricObject) => Boolean(obj.data?.isPermanent && !obj.data?.type?.includes('angle')) },
        { id: 'angle-measurements', name: 'Angle Measurements', type: 'angle',
          filter: (obj: CustomFabricObject) => obj.data?.type === 'angleMeasurement' },
        { id: 'skin-marker-drawings', name: 'Skin Marker Drawings', type: 'drawing',
          filter: (obj: CustomFabricObject) => obj.data?.type === 'drawing' }
      ];
      
      // Collections for each group type
      const groupedObjects: { [key: string]: CustomFabricObject[] } = {};
      
      // Regular layers (including individual shapes and other objects)
      const regularLayers: LayerInfo[] = [];
      
      // Process all objects and categorize them
      objects.forEach((obj, index) => {
        // Check if the object belongs to any group (but NOT incision templates)
        let isGrouped = false;
        
        // Skip grouping for objects that have the incision type but should be treated individually
        const isIndividualShape = obj.data?.type === 'incision' && obj.data?.name && obj.data.name.includes('Template');
        
        if (!isIndividualShape) {
          for (const group of groupDefinitions) {
            if (group.filter(obj)) {
              // Initialize the group array if it doesn't exist
              if (!groupedObjects[group.id]) {
                groupedObjects[group.id] = [];
              }
              // Add the object to its group
              groupedObjects[group.id].push(obj);
              isGrouped = true;
              break;
            }
          }
        }
        
        // If not grouped, it's a regular layer
        if (!isGrouped) {
          // Set object data.id if it doesn't exist to ensure consistent naming
          if (!obj.data?.id) {
            if (!obj.data) {
              obj.data = {};
            }
            obj.data.id = `layer-${index}`;
          }
          
          // Get or generate a stable name for this layer
          const layerName = getUniqueLayerName(obj, index);
          
          // If name is different than what's in obj.data.name, update it
          if (obj.data && (!obj.data.name || obj.data.name !== layerName)) {
            obj.data.name = layerName;
          }
          
          regularLayers.push({
            id: obj.data.id,
            type: obj.type || 'unknown',
            name: layerName,
            visible: obj.visible === true,
            zIndex: index,
            object: obj,
          });
        }
      });

      // Create groups based on the definitions
      const newGroups: GroupInfo[] = [];
      
      // Create groups dynamically based on our definitions
      groupDefinitions.forEach(groupDef => {
        const groupObjects = groupedObjects[groupDef.id] || [];
        if (groupObjects.length > 0) {
          const allVisible = groupObjects.every(obj => obj.visible === true);
          
          // Get custom name if it exists, otherwise use default
          let groupName = groupNamesRef.current.get(groupDef.id);
          
          // If no stored name, use the default
          if (!groupName) {
            groupName = groupDef.name;
          }
          
          // Add count to the name
          const displayName = `${groupName} (${groupObjects.length})`;
          
          newGroups.push({
            id: groupDef.id,
            name: displayName,
            visible: allVisible,
            type: groupDef.type,
            objects: groupObjects
          });
        }
      });
      
      // Update state directly
      setLayerState({
        regularLayers,
        groups: newGroups
      });
    };

    // Create a debounced function that will only execute after 100ms of inactivity
    updateLayersRef.current = debounce(processCanvasObjects, 100);

    // Function to call the debounced update
    const handleCanvasChange = () => {
      if (updateLayersRef.current) {
        updateLayersRef.current();
      }
    };

    // Initial update
    processCanvasObjects();

    // SIMPLIFIED EVENT HANDLING: Use only 3 core events
    canvas.on('object:added', handleCanvasChange);
    canvas.on('object:removed', handleCanvasChange);
    canvas.on('object:modified', handleCanvasChange);

    return () => {
      // Cancel any pending debounced calls
      if (updateLayersRef.current) {
        updateLayersRef.current.cancel();
      }
      
      // Remove all event listeners
      canvas.off('object:added', handleCanvasChange);
      canvas.off('object:removed', handleCanvasChange);
      canvas.off('object:modified', handleCanvasChange);
    };
  }, [canvas, getUniqueLayerName]);

  // Toggle visibility function for both layers and groups
  const toggleLayerVisibility = useCallback((layerId: string) => {
    if (!canvas) return;
    
    // Check for groups
    const group = layerState.groups.find(g => g.id === layerId);
    if (group) {
      const newVisibility = !group.visible;
      
      group.objects.forEach(obj => {
        obj.set('visible', newVisibility);
      });
      
      // Update the layerState with the new visibility for the group
      setLayerState(prevState => ({
        ...prevState,
        groups: prevState.groups.map(g => 
          g.id === layerId ? { ...g, visible: newVisibility } : g
        )
      }));
      
      canvas.renderAll();
      canvas.fire('object:modified');
      
      toast({
        title: "Group Visibility Updated",
        description: `${group.name} ${newVisibility ? 'shown' : 'hidden'}`,
      });
      return;
    }
    
    // Regular layer handling
    const layer = layerState.regularLayers.find(l => l.id === layerId);
    if (!layer) return;

    layer.object.set('visible', !layer.object.visible);
    
    // Update the layerState with the new visibility for the layer
    setLayerState(prevState => ({
      ...prevState,
      regularLayers: prevState.regularLayers.map(l => 
        l.id === layerId ? { ...l, visible: !l.visible } : l
      )
    }));
    
    canvas.renderAll();
    
    toast({
      title: "Layer Visibility Updated",
      description: `Layer ${layer.name} is now ${layer.object.visible ? 'visible' : 'hidden'}`,
    });
  }, [canvas, layerState]);

  // Rename layer function - updated to handle groups
  const renameLayer = useCallback((layerId: string, newName: string) => {
    if (!canvas) return;
    
    // Trim the input name
    const trimmedName = newName.trim();
    
    // Don't allow empty names
    if (!trimmedName) {
      toast({
        variant: "destructive",
        title: "Invalid Name",
        description: "Layer name cannot be empty.",
      });
      return;
    }
    
    // Check if it's a group
    const group = layerState.groups.find(g => g.id === layerId);
    if (group) {
      // Store the new name (without the count)
      groupNamesRef.current.set(layerId, trimmedName);
      
      // Update the group in state (with count)
      const objectCount = group.objects.length;
      const displayName = `${trimmedName} (${objectCount})`;
      
      setLayerState(prev => ({
        ...prev,
        groups: prev.groups.map(g => 
          g.id === layerId ? { ...g, name: displayName } : g
        )
      }));
      
      toast({
        title: "Group Renamed",
        description: `Group renamed to ${trimmedName}`,
      });
      
      // Request canvas update to reflect new names
      if (canvas) canvas.fire('object:modified');
      return;
    }
    
    // Regular layer handling
    const layer = layerState.regularLayers.find(l => l.id === layerId);
    if (!layer) return;

    if (!layer.object.data) {
      layer.object.data = {};
    }
    
    // Update the object's data
    layer.object.data.name = trimmedName;
    
    // Update our name tracking map
    layerNamesRef.current.set(layerId, trimmedName);
    
    // Update the state with the renamed layer
    setLayerState(prev => ({
      ...prev,
      regularLayers: prev.regularLayers.map(l => 
        l.id === layerId ? { ...l, name: trimmedName } : l
      )
    }));
    
    toast({
      title: "Layer Renamed",
      description: `Layer renamed to ${trimmedName}`,
    });
  }, [canvas, layerState]);

  // Updated group reordering function to handle multi-object groups properly
  const handleLayerReordering = useCallback((layerId: string, bringForward: boolean) => {
    if (!canvas) return;
    
    // Check if it's a group
    const isGroup = layerState.groups.some(g => g.id === layerId);
    
    // Handle group layer reordering
    if (isGroup) {
      const group = layerState.groups.find(g => g.id === layerId);
      if (!group || !group.objects.length) return;
      
      try {
        const allCanvasObjects = canvas.getObjects() as CustomFabricObject[];
        
        // Get z-indices of all group objects
        const objectIndices = group.objects.map(obj => allCanvasObjects.indexOf(obj));
        
        // Check if the group can be moved (is not already at top/bottom)
        if (bringForward) {
          // For "bring forward", check if any object in the group is at the top
          const maxIndex = Math.max(...objectIndices);
          if (maxIndex >= allCanvasObjects.length - 1) {
            toast({ 
              title: "Already at Top", 
              description: `${group.name} is already at the front`
            });
            return;
          }
        } else {
          // For "send backward", check if any object in the group is at the bottom
          const minIndex = Math.min(...objectIndices);
          if (minIndex <= 0) {
            toast({ 
              title: "Already at Bottom", 
              description: `${group.name} is already at the back`
            });
            return;
          }
        }
        
        // Get sorted indices to maintain relative order when moving
        const sortedIndices = [...objectIndices].sort(bringForward ? 
          // When moving forward, start from highest to lowest index
          (a, b) => b - a : 
          // When moving backward, start from lowest to highest index
          (a, b) => a - b
        );
        
        // Move each object in the group
        for (const index of sortedIndices) {
          const obj = allCanvasObjects[index];
          if (bringForward) {
            canvas.bringObjectForward(obj, true);
          } else {
            canvas.sendObjectBackwards(obj, true);
          }
        }
        
        canvas.renderAll();
        canvas.fire('object:modified');
        
        toast({
          title: bringForward ? "Group Moved Forward" : "Group Moved Backward",
          description: `${group.name} moved ${bringForward ? 'forward' : 'backward'}`
        });
      } catch (error) {
        console.error("Error moving group layer:", error);
        toast({
          variant: "destructive",
          title: "Error Moving Group",
          description: `Unable to move ${bringForward ? 'forward' : 'backward'}`
        });
      }
      return;
    }
    
    // Regular layer handling
    const layer = layerState.regularLayers.find(l => l.id === layerId);
    if (!layer) return;
    
    try {
      if (layer.object && canvas.contains(layer.object)) {
        const allObjects = canvas.getObjects();
        const objectIndex = allObjects.indexOf(layer.object);
        
        // Check if the object is already at the top or bottom
        const isAtTop = objectIndex === allObjects.length - 1;
        const isAtBottom = objectIndex === 0;
        
        // Prevent unnecessary movements and provide feedback
        if ((bringForward && isAtTop) || (!bringForward && isAtBottom)) {
          toast({
            title: bringForward ? "Already at Top" : "Already at Bottom",
            description: `Layer ${layer.name} is already ${bringForward ? 'at the front' : 'at the back'}`
          });
          return;
        }
        
        if (bringForward) {
          canvas.bringObjectForward(layer.object, true);
        } else {
          canvas.sendObjectBackwards(layer.object, true);
        }
        
        canvas.renderAll();
        canvas.fire('object:modified');
        
        toast({
          title: bringForward ? "Layer Moved Forward" : "Layer Moved Backward",
          description: `Layer ${layer.name} moved ${bringForward ? 'forward' : 'backward'}`
        });
      }
    } catch (error) {
      console.error("Error moving layer:", error);
      toast({
        variant: "destructive",
        title: "Error Moving Layer",
        description: `Unable to move layer ${bringForward ? 'forward' : 'backward'}`
      });
    }
  }, [canvas, layerState]);

  // Convenience methods that use the shared handler
  const bringLayerForward = useCallback((layerId: string) => {
    handleLayerReordering(layerId, true);
  }, [handleLayerReordering]);

  const sendLayerBackward = useCallback((layerId: string) => {
    handleLayerReordering(layerId, false);
  }, [handleLayerReordering]);

  // Order layers consistently: groups first, then regular layers in reverse order
  const orderedLayers = useCallback((): DisplayLayerInfo[] => {
    // Regular layers are shown in reverse order (so newer/higher z-index layers appear at the top)
    const regularLayersReversed = [...layerState.regularLayers].reverse();
    
    return [...layerState.groups, ...regularLayersReversed];
  }, [layerState]);

  return {
    layers: orderedLayers(),
    toggleLayerVisibility,
    renameLayer,
    bringLayerForward,
    sendLayerBackward,
  };
};
